<template>
  <div>
    <finance-payouts-totals :totals="totals" />

    <b-overlay :show="isLoading" rounded="sm">
      <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap">
        <b-tabs v-model="kindFilterIndex" pills class="mb-0">
          <b-tab v-for="t in kindTabs" :key="t.label" :title="t.label" />
        </b-tabs>
        <b-button variant="outline-primary" size="sm" @click="copyList">
          <feather-icon icon="CopyIcon" class="mr-50" />
          Скопировать список
        </b-button>
      </div>

      <b-alert v-if="!recipients.length && !isLoading" show variant="success" class="mb-0">
        <feather-icon icon="CheckCircleIcon" class="mr-50" />
        Все обязательства закрыты
      </b-alert>
      <b-alert
        v-else-if="!filteredRecipients.length && !isLoading"
        show
        variant="secondary"
        class="mb-0"
      >
        Нет получателей в этой категории
      </b-alert>

      <finance-payouts-recipient
        v-for="(item, idx) in filteredRecipients"
        :key="`${item.recipient.id}-${idx}`"
        :item="item"
      />
    </b-overlay>
  </div>
</template>

<script>
import {
  BTabs, BTab, BButton, BOverlay, BAlert,
} from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FinancePayoutsTotals from '@/views/finance/components/FinancePayoutsTotals.vue'
import FinancePayoutsRecipient from '@/views/finance/components/FinancePayoutsRecipient.vue'
import { formatRub } from '@/views/finance/utils'

const KIND_TABS = [
  { label: 'Все', kind: null },
  { label: 'Ведущим', kind: 'host' },
  { label: 'Авторам', kind: 'author' },
  { label: 'Фиксированные', kind: 'fixed' },
  { label: 'Прочее', kind: 'extra' },
]

const KIND_LABELS_RU = { host: 'Ведущий', author: 'Автор', fixed: 'Фиксированная', extra: 'Прочее' }
const KIND_ORDER = { host: 0, author: 1, fixed: 2, extra: 3 }

export default {
  components: {
    BTabs, BTab, BButton, BOverlay, BAlert,
    FinancePayoutsTotals, FinancePayoutsRecipient,
  },
  setup() {
    const toast = useToast()
    const isLoading = ref(false)
    const recipients = ref([])
    const totals = ref(null)
    const kindFilterIndex = ref(0)
    const kindTabs = KIND_TABS

    const filteredRecipients = computed(() => {
      const selectedKind = KIND_TABS[kindFilterIndex.value].kind
      const list = selectedKind ? recipients.value.filter(r => r.kind === selectedKind) : recipients.value

      const map = new Map()
      list.forEach((entry, index) => {
        const r = entry.recipient
        let key
        if (r && r.id != null) key = `id:${r.id}`
        else if (r && r.name) key = `name:${r.name}`
        else key = `solo:${index}`
        const recipient = r || { id: null, name: KIND_LABELS_RU[entry.kind] || 'Без получателя' }
        if (!map.has(key)) {
          map.set(key, {
            recipient, total: 0, itemsCount: 0, sections: [],
          })
        }
        const group = map.get(key)
        group.total += entry.total
        group.itemsCount += entry.items.length
        group.sections.push({ kind: entry.kind, total: entry.total, items: entry.items })
      })

      const groups = [...map.values()]
      groups.forEach(g => {
        g.sections.sort((a, b) => (KIND_ORDER[a.kind] ?? 99) - (KIND_ORDER[b.kind] ?? 99))
      })
      return groups.sort((a, b) => b.total - a.total)
    })

    const fetchPayouts = () => {
      isLoading.value = true
      store.dispatch('app-finance/fetchPayouts')
        .then(response => {
          recipients.value = response.data.data.recipients || []
          totals.value = response.data.data.totals || null
        })
        .catch(err => {
          toast({
            component: ToastificationContent,
            props: {
              title: err.response?.data?.message || 'Ошибка загрузки финансового отчёта',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const copyList = () => {
      const groups = filteredRecipients.value
      if (!groups.length) return
      const blocks = groups.map(group => {
        const head = `${group.recipient.name} — ${formatRub(group.total)}`
        const sectionLines = group.sections.map(section => `  ${KIND_LABELS_RU[section.kind] || section.kind} — ${formatRub(section.total)}`)
        return [head, ...sectionLines].join('\n')
      })
      const total = groups.reduce((acc, g) => acc + g.total, 0)
      const text = `${blocks.join('\n\n')}\n\nИтого: ${formatRub(total)}`
        // Заменяем неразрывные пробелы (U+00A0, U+202F) из Intl-форматирования на обычные
        .replace(/[\u00A0\u202F]/g, ' ')
      navigator.clipboard.writeText(text).then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Список скопирован',
            icon: 'CopyIcon',
            variant: 'success',
          },
        })
      })
    }

    fetchPayouts()

    return {
      isLoading, recipients, totals, kindFilterIndex, kindTabs, filteredRecipients, copyList,
    }
  },
}
</script>
